import { useEffect, useState } from 'react';
import { DndContext, closestCenter } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { supabase } from '../../lib/supabase';
import { toast } from 'sonner';
import { confirmDialog } from '../../lib/confirmDialog';
import { validatePortalUrl } from '../../lib/urlValidator';

const PLATFORM_OPTIONS = ['Instagram', 'Facebook', 'Twitter', 'YouTube', 'TikTok', 'Other'];

export default function SocialFeedSectionBuilder({ teamId, sectionId, items = [], onItemsUpdate }) {
  const [localItems, setLocalItems] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [newItem, setNewItem] = useState({
    platform: PLATFORM_OPTIONS[0],
    displayName: '',
    profileUrl: '',
    embedHtml: ''
  });

  useEffect(() => {
    const filtered = items.filter((item) => item.section_id === sectionId).sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
    setLocalItems(filtered);
  }, [items, sectionId]);

  const canMutate = Boolean(teamId && sectionId);

  const handleAddItem = async () => {
    if (!canMutate) {
      toast.error('Please save the section before adding social links.');
      return;
    }

    if (!newItem.profileUrl.trim() && !newItem.embedHtml.trim()) {
      toast.error('Provide a profile URL or embed code.');
      return;
    }

    let normalizedUrl = null;
    if (newItem.profileUrl.trim()) {
      const { valid, normalizedUrl: url, error } = validatePortalUrl(newItem.profileUrl.trim());
      if (!valid) {
        toast.error(error || 'Invalid link');
        return;
      }
      normalizedUrl = url;
    }

    setSaving(true);
    try {
      const insertPayload = {
        team_id: teamId,
        section_id: sectionId,
        platform: newItem.platform,
        display_name: newItem.displayName.trim() || null,
        profile_url: normalizedUrl,
        embed_html: newItem.embedHtml.trim() || null,
        sort_order: localItems.length,
        visible: true
      };

      const { data, error } = await supabase
        .from('portal_social_links')
        .insert(insertPayload)
        .select()
        .single();

      if (error) throw error;

      const updated = [...localItems, data];
      setLocalItems(updated);
      onItemsUpdate?.(sectionId, updated);
      setNewItem({ platform: PLATFORM_OPTIONS[0], displayName: '', profileUrl: '', embedHtml: '' });
      setIsAdding(false);
      toast.success('Social feed item added');
    } catch (error) {
      console.error('Error adding social feed item:', error);
      toast.error('Failed to add social feed item: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateItem = async (itemId, updates) => {
    const payload = { ...updates };
    if (payload.profile_url) {
      const { valid, normalizedUrl, error } = validatePortalUrl(payload.profile_url);
      if (!valid) {
        toast.error(error || 'Invalid link');
        return;
      }
      payload.profile_url = normalizedUrl;
    }

    setSaving(true);
    try {
      const { error } = await supabase
        .from('portal_social_links')
        .update(payload)
        .eq('id', itemId);

      if (error) throw error;

      const updated = localItems.map((item) => (item.id === itemId ? { ...item, ...payload } : item));
      setLocalItems(updated);
      onItemsUpdate?.(sectionId, updated);
      setEditingId(null);
      toast.success('Social feed item updated');
    } catch (error) {
      console.error('Error updating social feed item:', error);
      toast.error('Failed to update social feed item: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteItem = async (itemId) => {
    const confirmed = await confirmDialog('Remove this social feed item?', {
      confirmText: 'Delete',
      cancelText: 'Cancel'
    });
    if (!confirmed) return;

    setSaving(true);
    try {
      const { error } = await supabase
        .from('portal_social_links')
        .delete()
        .eq('id', itemId);

      if (error) throw error;

      const updated = localItems.filter((item) => item.id !== itemId);
      setLocalItems(updated);
      onItemsUpdate?.(sectionId, updated);
      toast.success('Social feed item removed');
    } catch (error) {
      console.error('Error deleting social feed item:', error);
      toast.error('Failed to delete social feed item: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleVisibility = async (item) => {
    setSaving(true);
    try {
      const nextVisible = !item.visible;
      const { error } = await supabase
        .from('portal_social_links')
        .update({ visible: nextVisible })
        .eq('id', item.id);

      if (error) throw error;

      const updated = localItems.map((link) =>
        link.id === item.id ? { ...link, visible: nextVisible } : link
      );
      setLocalItems(updated);
      onItemsUpdate?.(sectionId, updated);
      toast.success(`Social item ${nextVisible ? 'shown' : 'hidden'} on portal`);
    } catch (error) {
      console.error('Error toggling social feed visibility:', error);
      toast.error('Failed to update visibility: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleReorder = async (activeId, overId) => {
    if (activeId === overId) return;

    const fromIndex = localItems.findIndex((item) => item.id === activeId);
    const toIndex = localItems.findIndex((item) => item.id === overId);
    if (fromIndex === -1 || toIndex === -1) return;

    const reordered = [...localItems];
    const [moved] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, moved);

    const updates = reordered.map((item, idx) => ({ ...item, sort_order: idx }));
    setLocalItems(updates);

    setSaving(true);
    try {
      for (const item of updates) {
        await supabase
          .from('portal_social_links')
          .update({ sort_order: item.sort_order })
          .eq('id', item.id);
      }
      onItemsUpdate?.(sectionId, updates);
    } catch (error) {
      console.error('Error reordering social feed items:', error);
      toast.error('Failed to reorder social feed items');
      setLocalItems(localItems);
    } finally {
      setSaving(false);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!active || !over) return;
    handleReorder(active.id, over.id);
  };

  return (
    <div className="space-y-3" aria-busy={saving}>
      <div className="flex items-center justify-between mb-3">
        <label className="block text-sm font-medium text-gray-700">Social Feed Items</label>
        {!isAdding && (
          <button
            onClick={() => setIsAdding(true)}
            className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
          >
            + Add Social Item
          </button>
        )}
      </div>

      {isAdding && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
          <select
            value={newItem.platform}
            onChange={(e) => setNewItem((prev) => ({ ...prev, platform: e.target.value }))}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {PLATFORM_OPTIONS.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Display name (optional)"
            value={newItem.displayName}
            onChange={(e) => setNewItem((prev) => ({ ...prev, displayName: e.target.value }))}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <input
            type="text"
            placeholder="Profile URL (optional)"
            value={newItem.profileUrl}
            onChange={(e) => setNewItem((prev) => ({ ...prev, profileUrl: e.target.value }))}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <textarea
            placeholder="Embed HTML (optional — paste Instagram/Twitter embed code)"
            value={newItem.embedHtml}
            onChange={(e) => setNewItem((prev) => ({ ...prev, embedHtml: e.target.value }))}
            rows={3}
            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <div className="flex gap-2">
            <button
              onClick={handleAddItem}
              disabled={saving}
              className="flex-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 transition-colors"
            >
              {saving ? 'Saving...' : 'Add Item'}
            </button>
            <button
              onClick={() => {
                setIsAdding(false);
                setNewItem({ platform: PLATFORM_OPTIONS[0], displayName: '', profileUrl: '', embedHtml: '' });
              }}
              disabled={saving}
              className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {localItems.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-4">
          No social links added yet. Pull in your latest posts to keep families connected.
        </p>
      ) : (
        <DndContext collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={localItems.map((item) => item.id)} strategy={verticalListSortingStrategy}>
            <div className="space-y-2">
              {localItems.map((item) => (
                <SortableSocialCard key={item.id} item={item}>
                  {({ attributes, listeners }) => (
                    editingId === item.id ? (
                      <SocialEditForm
                        item={item}
                        saving={saving}
                        onCancel={() => setEditingId(null)}
                        onSave={(updates) => handleUpdateItem(item.id, updates)}
                      />
                    ) : (
                      <div className="flex gap-3">
                        <button
                          {...attributes}
                          {...listeners}
                          className="px-2 py-2 text-xs text-gray-500 hover:bg-gray-100 rounded self-start"
                          title="Drag to reorder"
                          aria-label="Drag social item to reorder"
                          type="button"
                        >
                          ☰
                        </button>
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="inline-flex items-center px-2 py-0.5 text-[11px] rounded bg-purple-100 text-purple-700">
                              {item.platform || 'Social'}
                            </span>
                            {item.visible === false && (
                              <span className="inline-flex items-center px-2 py-0.5 text-[11px] rounded bg-gray-200 text-gray-600">
                                Hidden
                              </span>
                            )}
                          </div>
                          <h4 className="font-medium text-gray-900 text-sm mt-1">
                            {item.display_name || 'Untitled social item'}
                          </h4>
                          {item.profile_url && (
                            <a
                              href={item.profile_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-blue-600 hover:underline break-all mt-1 inline-block"
                            >
                              {item.profile_url}
                            </a>
                          )}
                          {item.embed_html && (
                            <p className="text-[11px] text-gray-400 mt-2">
                              Embed code provided
                            </p>
                          )}
                          <div className="flex gap-1 mt-2">
                            <button
                              onClick={() => setEditingId(item.id)}
                              disabled={saving}
                              className="text-xs px-2 py-1 bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => handleToggleVisibility(item)}
                              disabled={saving}
                              className={`text-xs px-2 py-1 rounded ${
                                item.visible
                                  ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                              } disabled:opacity-50`}
                            >
                              {item.visible ? '✓ Visible' : '✕ Hidden'}
                            </button>
                            <button
                              onClick={() => handleDeleteItem(item.id)}
                              disabled={saving}
                              className="text-xs px-2 py-1 bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </SortableSocialCard>
              ))}
            </div>
          </SortableContext>
        </DndContext>
      )}

      <p className="text-xs text-gray-500">
        Use embed codes from Instagram, X, or TikTok to showcase live social content, or link directly to team profiles.
      </p>
    </div>
  );
}

function SortableSocialCard({ item, children }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: item.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    cursor: isDragging ? 'grabbing' : 'grab'
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border rounded-lg p-3 ${item.visible ? 'bg-white border-gray-200' : 'bg-gray-100 border-gray-300'} ${
        isDragging ? 'shadow-lg z-10' : ''
      }`}
    >
      {children({ attributes, listeners })}
    </div>
  );
}

function SocialEditForm({ item, onSave, onCancel, saving }) {
  const [platform, setPlatform] = useState(item.platform || PLATFORM_OPTIONS[0]);
  const [displayName, setDisplayName] = useState(item.display_name || '');
  const [profileUrl, setProfileUrl] = useState(item.profile_url || '');
  const [embedHtml, setEmbedHtml] = useState(item.embed_html || '');

  const handleSubmit = () => {
    if (!profileUrl.trim() && !embedHtml.trim()) {
      toast.error('Provide a profile URL or embed code.');
      return;
    }
    onSave({
      platform,
      display_name: displayName.trim() || null,
      profile_url: profileUrl.trim() || null,
      embed_html: embedHtml.trim() || null
    });
  };

  return (
    <div className="space-y-2">
      <select
        value={platform}
        onChange={(e) => setPlatform(e.target.value)}
        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
      >
        {PLATFORM_OPTIONS.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
      <input
        type="text"
        placeholder="Display name"
        value={displayName}
        onChange={(e) => setDisplayName(e.target.value)}
        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <input
        type="text"
        placeholder="Profile URL"
        value={profileUrl}
        onChange={(e) => setProfileUrl(e.target.value)}
        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <textarea
        placeholder="Embed HTML"
        value={embedHtml}
        onChange={(e) => setEmbedHtml(e.target.value)}
        rows={3}
        className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
      />
      <div className="flex gap-2">
        <button
          onClick={handleSubmit}
          disabled={saving}
          className="flex-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save'}
        </button>
        <button
          onClick={onCancel}
          disabled={saving}
          className="px-3 py-1.5 text-sm bg-gray-200 text-gray-700 rounded hover:bg-gray-300 disabled:opacity-50"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

